import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { notifyError } from '../../../../shared/errorMessage'
import { TApi } from '../../../../shared/const'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'

import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'
import Search from '../../../../UI/Search'
import BtnFilter from '../../../../UI/BtnFilter'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { targetGroupField } from '../../../../UI/FilterPanel/fields'

import OverviewGantt from './OverviewGantt'
import TemplateList from './TemplateList'
import TemplateDetail from './TemplateDetail'
import { ADMIN_KEY, targetGroupOptions } from './TargetGroupFilter'
import styles from './style.module.scss'

/*
 * Kommunikations-Verwaltung im Admin-Bereich.
 *
 * Drei Register: die Übersicht als Balkenplan über den Lebenszyklus einer Junior
 * Company, dann E-Mail-Vorlagen und interne Nachrichten. Beide Vorlagenarten
 * kommen als Katalog + Overlay vom Server und sind bearbeitbar.
 *
 * Die Dokumentvorlagen (PDF/Urkunden) standen hier als weiteres Register und
 * liegen seit 04.08.2026 unter Einstellungen → Dokumente: Sie sind nicht
 * bearbeitbar, sondern eine Nachschau — und damit näher an den Einstellungen
 * als an den Vorlagen, die man hier tatsächlich ändert. Die Bausteine dafür
 * (DocumentList, DocumentDetail) bleiben in diesem Ordner, weil sie sich
 * Zielgruppenfilter und Stile mit den Vorlagenlisten teilen.
 *
 * In der Übersicht stehen beide Kanäle nebeneinander. Die Detailansicht wird
 * deshalb dort mit dem Kanal der angeklickten Zeile geöffnet und nicht mit dem
 * des Registers — der Weg zurück führt wieder in die Übersicht.
 *
 * Suche und Zielgruppenfilter gehören der Seite, nicht der Vorlagenliste: Sie
 * stehen rechts oben in der Kopfleiste bzw. in der Filterleiste, die von rechts
 * hereinfährt (UI/FilterPanel) — dieselbe wie in allen anderen Listen. In der
 * Übersicht und in der Detailansicht gibt es nichts zu filtern; dort sind sie
 * ausgeblendet statt wirkungslos dazustehen.
 */

const TABS = {
	OVERVIEW: 'overview',
	EMAIL: 'email',
	INTERNAL: 'internal'
}

const LIST_URLS = {
	[TABS.OVERVIEW]: TApi.ADMIN_COMM_OVERVIEW,
	[TABS.EMAIL]: TApi.ADMIN_COMM_EMAILS,
	[TABS.INTERNAL]: TApi.ADMIN_COMM_INTERNAL
}

// Tiefer Link: ?tab=internal&item=<key> öffnet direkt das passende Register und
// die Detailansicht.
const initialFromQuery = () => {
	if (typeof window === 'undefined') return { tab: TABS.OVERVIEW, item: null }
	const params = new URLSearchParams(window.location.search)
	const qtab = params.get('tab')
	return {
		tab: Object.values(TABS).includes(qtab) ? qtab : TABS.OVERVIEW,
		item: params.get('item') || null
	}
}

export default function Communication() {
	const { t } = useTranslation()

	const [tab, setTab] = useState(() => initialFromQuery().tab)
	const [list, setList] = useState(null)
	const [selectedKey, setSelectedKey] = useState(() => initialFromQuery().item)
	// Nur in der Übersicht abweichend vom Register: dort bestimmt die angeklickte
	// Zeile, ob eine E-Mail oder eine interne Nachricht geöffnet wird.
	const [selectedChannel, setSelectedChannel] = useState(TABS.EMAIL)

	const [search, setSearch] = useState('')
	const [showFilter, setShowFilter] = useState(false)

	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)

	/*
	 * Die geladenen Daten tragen das Register, zu dem sie gehoeren.
	 *
	 * Ohne diesen Vermerk zeigte ein Registerwechsel fuer einen Wimpernschlag die
	 * Daten des vorigen Registers in der neuen Ansicht — das Laden faengt erst
	 * nach dem Rendern an. Solange alle drei Register Listen lieferten, fiel das
	 * nicht auf. Die Uebersicht liefert aber ein Objekt: Der Wechsel von dort in
	 * die Vorlagenliste gab dieses Objekt an eine Ansicht weiter, die eine Liste
	 * erwartet, und riss die ganze Seite ab ("Invalid attempt to iterate
	 * non-iterable instance", weisse Seite).
	 */
	const fetchList = useCallback(async () => {
		setList(null)
		try {
			// `disableToast`: Der eigene Satz benennt, was nicht geladen werden konnte.
			const { data } = await http.get(LIST_URLS[tab], { disableToast: true })
			setList({ tab, data })
		} catch (error) {
			notifyError(error, 'Communication templates could not be loaded')
			setList({ tab, data: [] })
		}
		// t bewusst NICHT in den Abhaengigkeiten: useTranslation liefert bei jedem
		// Render eine neue Funktionsreferenz. Mit t in der Liste entstuende die
		// Schleife Render -> Laden -> Render (dieselbe Falle wie in den
		// Admin-Listen und in TemplateDetail).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab])

	useEffect(() => {
		fetchList()
	}, [fetchList])

	/*
	 * Beim Registerwechsel die offene Detailansicht schließen — und die geladene
	 * Liste gleich mit verwerfen.
	 *
	 * Ohne das Verwerfen gab es einen Render, in dem schon das neue Register galt,
	 * aber noch die Daten des alten dastanden: Die Übersicht liefert ein Objekt,
	 * die Vorlagenliste erwartet eine Reihung — die Seite blieb weiß, bis man neu
	 * lud (auf der Testumgebung nachgestellt, 05.08.2026).
	 */
	const switchTab = next => {
		if (next === tab) return
		setSelectedKey(null)
		setList(null)
		setTab(next)
		// Die Leiste gehoert zum Register, das man verlaesst — offen stehen zu
		// bleiben, waehrend der Knopf dazu verschwindet, waere eine Sackgasse.
		setShowFilter(false)
	}

	const openFromOverview = (channel, key) => {
		setSelectedChannel(channel)
		setSelectedKey(key)
	}

	// Querverweis: zum Gegenstück im anderen Register springen (E-Mail ↔ intern).
	// Aus der Übersicht heraus bleibt man in der Übersicht.
	const openRelated = (nextChannel, key) => {
		if (tab === TABS.OVERVIEW) {
			openFromOverview(nextChannel, key)
			return
		}
		setTab(nextChannel === 'email' ? TABS.EMAIL : TABS.INTERNAL)
		setSelectedKey(key)
	}

	const detailChannel = tab === TABS.OVERVIEW ? selectedChannel : tab

	// Nur die Daten des aktuellen Registers durchreichen; alles andere ist der
	// Ladezustand.
	const daten = list && list.tab === tab ? list.data : null

	/*
	 * Die Zielgruppen entstehen aus dem geladenen Katalog: Kommt eine Gruppe
	 * dazu, taucht sie hier von selbst auf. In der Übersicht liegt kein Katalog
	 * vor — dann bleibt das Feld leer und die Leiste ist ohnehin ausgeblendet.
	 */
	const fields = useMemo(
		() => [
			targetGroupField(
				t,
				targetGroupOptions(Array.isArray(daten) ? daten : []).map(group => ({
					value: group,
					label: group === ADMIN_KEY ? t('Administration') : group
				}))
			)
		],
		[t, daten]
	)

	const filters = useFilters({ storageKey: 'communication', fields })
	const debouncedSearch = useDebouncedValue(search)

	// Gefiltert wird nur in den Vorlagenlisten — nicht in der Übersicht und
	// nicht, solange eine Vorlage offen ist.
	const zeigtListe = !selectedKey && tab !== TABS.OVERVIEW && Array.isArray(daten)

	const renderTab = (value, label) => (
		<button
			type="button"
			className={clsx(styles.tab, tab === value && styles.tabActive)}
			onClick={() => switchTab(value)}
		>
			{label}
		</button>
	)

	return (
		<div className={styles.root}>
			<MiniHeader title={t('communication')}>
				{zeigtListe && (
					<div className={styles.headerContent}>
						<Search
							value={search}
							onChange={e => setSearch(e.target.value)}
							placeholder={t('Search templates...')}
							onSubmit={e => e.preventDefault()}
						/>
						<BtnFilter
							icon="arrow"
							onClick={() => setShowFilter(true)}
							count={filters.count}
						>
							{t('Filter')} ({filters.count})
						</BtnFilter>
					</div>
				)}
			</MiniHeader>

			<div className={styles.tabs} ref={tabsRef}>
				{renderTab(TABS.OVERVIEW, t('Overview'))}
				{renderTab(TABS.EMAIL, t('Email templates'))}
				{renderTab(TABS.INTERNAL, t('Internal messages'))}
			</div>

			{daten === null ? (
				<Loading />
			) : selectedKey ? (
				<TemplateDetail
					key={`${detailChannel}:${selectedKey}`}
					channel={detailChannel}
					templateKey={selectedKey}
					onBack={() => setSelectedKey(null)}
					onSaved={fetchList}
					onOpenRelated={openRelated}
				/>
			) : tab === TABS.OVERVIEW ? (
				<OverviewGantt data={daten} onOpen={openFromOverview} />
			) : /* Zweiter Riegel gegen die Daten des vorigen Registers. */
			Array.isArray(daten) ? (
				<TemplateList
					channel={tab}
					items={daten}
					search={debouncedSearch}
					targetGroups={filters.query.targetGroup || []}
					onOpen={setSelectedKey}
				/>
			) : (
				<Loading />
			)}

			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={filters.setField}
				onResetAll={filters.resetAll}
			/>
		</div>
	)
}
